import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import DisplayAnnouncements from './components/listView/DisplayAnnouncements';
import {
  getListAnnouncementsStart,
  deleteAnnouncementStart,
  updateAnnouncementStart,
  addAnnouncementStart
} from 'redux/announcement/actions';
import { DataTableSkeleton } from 'carbon-components-react';
import {
  selectListAnnouncements,
  selectSuccess,
  selectProcess
} from 'redux/announcement/selectors';
import { FullHeaderLeftSideFullPanelLayout } from 'layouts/fullHeader.LeftSideFullPanel.layout';
import AddAnnouncement from './components/addAnnouncements/AddAnnouncement';
import { APP } from 'app/AppRoutes';
import { Params } from 'types';
import { AddAnnouncementRequest } from 'redux/announcement/types';
import { currentUser } from 'redux/session/selectors';
import Utils from 'utilities/helper.util';

const PAGE_SIZE = 10;

const ManageAnnouncements: React.FC<RouteComponentProps<{}, any, any>> = () => {
  const dispatch: any = useDispatch();
  const history = useHistory();
  const ref = useRef(null);

  const announcementsData = useSelector(selectListAnnouncements);
  const user = useSelector(currentUser);
  const isSuccess = useSelector(selectSuccess);
  const process = useSelector(selectProcess);

  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE);
  const [pageOffset, setPageOffset] = useState<number>(0);
  const [announcements, setAnnouncements] = useState([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [isSidePanelOpen, setIsSidePanelOpen] = useState<boolean>(false);
  const [refreshData, setRefreshData] = useState<boolean>(false);
  const [editId, setEditId] = useState<number>(0);
  const [editData, setEditData] = useState<Object>({});

  const isAADBYD = !user?.Organisation?.tenantId;

  // only AADBYDADMIN and SYSADMIN can access this page
  if (user?.role === 'DBYDADMIN' && !isAADBYD) {
    history.push('/dashboard');
  }

  const onClickCloseButton = () => {
    setEditData({});
    setEditId(0);
    setIsSidePanelOpen(false);
  };

  const handlePageChange = ({
    page,
    pageSize
  }: {
    page: number;
    pageSize: number;
  }) => {
    setPageOffset((page - 1) * pageSize);
    setCurrentPage(page);
    setPageSize(pageSize);
  };

  const handleAddAnnouncementButtonClick = () => {
    setIsSidePanelOpen(true);
  };

  const handlePreviewEdit = (id: any) => {
    setIsSidePanelOpen(true);
    setEditId(id);
    if (announcements && id) {
      const data = announcements.find((ele: any) => {
        return ele.id === Number(id);
      });
      data && setEditData(data);
    }
  };

  const handleMakeActive = (id: any) => {
    const today = Utils.getNewDate().startOf('day').utc().format();
    const payload = {
      id,
      params: {
        startAt: today
      }
    };
    dispatch(updateAnnouncementStart(payload));
  };

  const handleDelete = (id: number, status: string) => {
    if (status === 'Scheduled') {
      dispatch(deleteAnnouncementStart(id));
    } else if (status === 'Active') {
      const now = Utils.getNewDate().utc().format();
      const payload = {
        id,
        params: {
          endAt: now
        }
      };
      dispatch(updateAnnouncementStart(payload));
    }
  };

  const handleOnSubmit = (addAnnouncementObject: AddAnnouncementRequest) => {
    if (editId && editData) {
      const payload = {
        id: editId,
        params: {
          ...addAnnouncementObject,
          callToActionLabel: !addAnnouncementObject.callToActionLabel
            ? undefined
            : addAnnouncementObject.callToActionLabel,
          callToActionLink: !addAnnouncementObject.callToActionLink
            ? undefined
            : addAnnouncementObject.callToActionLink
        }
      };
      dispatch(updateAnnouncementStart(payload));
    } else {
      const params = {
        ...addAnnouncementObject,
        callToActionLabel: !addAnnouncementObject.callToActionLabel
          ? undefined
          : addAnnouncementObject.callToActionLabel,
        callToActionLink: !addAnnouncementObject.callToActionLink
          ? undefined
          : addAnnouncementObject.callToActionLink
      };
      dispatch(addAnnouncementStart(params));
    }
  };

  const setToRefresh = () => {
    setIsSidePanelOpen(false);
    setEditId(0);
    setEditData({});
    setPageOffset(0);
    setCurrentPage(1);
    setAnnouncements([]);
    setTotalItems(0);
    setRefreshData(true);
  };

  useEffect(() => {
    if (announcementsData) {
      setAnnouncements(announcementsData[0]?.Notices);
      setTotalItems(announcementsData[0]?.Info?.count || 0);
    }
  }, [announcementsData]);

  const fetchData = useCallback(() => {
    const queryParams: Params = {
      fields: '*',
      limit: pageSize,
      offset: pageOffset,
      orderBy: '-endAt',
      include: 'Organisation'
    };

    dispatch(getListAnnouncementsStart(queryParams));
    setRefreshData(false);
  }, [dispatch, pageSize, pageOffset]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshData]);

  useEffect(() => {
    if (
      (process === 'delete' || process === 'update' || process === 'add') &&
      isSuccess
    ) {
      setToRefresh();
    }
  }, [process, isSuccess]);

  return (
    <div id="ManageAnnouncements" data-testid="ManageAnnouncements" ref={ref}>
      <FullHeaderLeftSideFullPanelLayout
        pageHeading="Manage announcements"
        pageDescription="Select and manage active announcements or create new ones"
        enableHeaderGlobalBar={true}
      >
        <div id="members__body-content1" className="members__body-content1">
          {!(announcements?.length > 0) || (process && !isSuccess) ? (
            <DataTableSkeleton showHeader={false} />
          ) : (
            <div id="DisplayAnnouncements" data-testid="DisplayAnnouncements">
              <DisplayAnnouncements
                announcements={announcements}
                handlePreviewEdit={handlePreviewEdit}
                handleMakeActive={handleMakeActive}
                handleDelete={handleDelete}
                data-testid="DisplayAnnouncements"
                handlePageChange={handlePageChange}
                currentPage={currentPage}
                pageSize={pageSize}
                totalItems={totalItems}
                handleAddAnnouncement={handleAddAnnouncementButtonClick}
              />
            </div>
          )}
          {isSidePanelOpen &&
            ReactDOM.createPortal(
              <AddAnnouncement
                handleCancel={onClickCloseButton}
                handleOnSubmit={handleOnSubmit}
                editId={editId}
                editData={editData}
              />,
              document.querySelector(`#${APP}`) || document.body
            )}
        </div>
      </FullHeaderLeftSideFullPanelLayout>
    </div>
  );
};

ManageAnnouncements.displayName = 'ManageAnnouncements';
export default ManageAnnouncements;
